import { useState } from 'react'
import { ApiError } from '../api/client'
import { downloadTextFile } from '../export/download'
import { useAnonymizeCustomer, useDeleteCustomer, useExportCustomer } from './useCustomers'

/**
 * DSGVO-Aktionen am Kundendetail: Datenauskunft exportieren, anonymisieren (Löschpfad ohne Datenverlust)
 * und hart löschen. Löschen ist GoBD-gesperrt, wenn der Kunde Aufträge hat — der Server antwortet 409
 * {blocked:'has_orders'}, dann bietet die UI die Anonymisierung an (kein stiller Datenverlust). Die
 * destruktiven Schritte sind mit einer Rückfrage abgesichert.
 */
export function CustomerDsgvo({ id, name, onDeleted }: { id: number; name: string; onDeleted: () => void }) {
  const exportCustomer = useExportCustomer(id)
  const anonymize = useAnonymizeCustomer(id)
  const remove = useDeleteCustomer(id)
  const [blocked, setBlocked] = useState(false)

  function runExport(): void {
    exportCustomer.mutate(undefined, {
      onSuccess: (data) => downloadTextFile(`kunde-${id}-dsgvo-export.json`, JSON.stringify(data, null, 2), 'application/json'),
    })
  }

  function runAnonymize(): void {
    if (!window.confirm(`„${name}" anonymisieren? Die Stammdaten werden unkenntlich gemacht; die Aufträge bleiben (gesetzliche Aufbewahrung).`)) return
    setBlocked(false)
    anonymize.mutate()
  }

  function runDelete(): void {
    if (!window.confirm(`„${name}" endgültig löschen?`)) return
    setBlocked(false)
    remove.mutate(undefined, {
      onSuccess: onDeleted,
      onError: (error) => {
        if (error instanceof ApiError && error.status === 409) setBlocked(true)
      },
    })
  }

  return (
    <section className="wf-dsgvo">
      <h3 className="wf-dsgvo__title">Datenschutz (DSGVO)</h3>
      <div className="wf-dsgvo__actions">
        <button type="button" className="wf-btn wf-btn--ghost" disabled={exportCustomer.isPending} onClick={runExport}>
          Daten exportieren
        </button>
        <button type="button" className="wf-btn wf-btn--ghost" disabled={anonymize.isPending} onClick={runAnonymize}>
          Anonymisieren
        </button>
        <button type="button" className="wf-btn wf-btn--danger" disabled={remove.isPending} onClick={runDelete}>
          Löschen
        </button>
      </div>
      {blocked ? (
        <p className="warn" role="alert">
          Dieser Kunde hat Aufträge und darf nicht gelöscht werden (gesetzliche Aufbewahrung). Stattdessen anonymisieren.
        </p>
      ) : null}
      {exportCustomer.error instanceof ApiError ? (
        <p className="error" role="alert">
          Export fehlgeschlagen: {exportCustomer.error.message}
        </p>
      ) : null}
      {anonymize.error instanceof ApiError ? (
        <p className="error" role="alert">
          {anonymize.error.message}
        </p>
      ) : null}
    </section>
  )
}
